import React, { useState } from "react";
import ProductSearch from "../components/ProductSearch/ProductSearch";
import ProductStoreMatrix from "../components/ProductStoreMatrix/ProductStoreMatrix";
import { Box, Typography, Divider } from "@mui/material";
import CustomDialog from "../components/CustomDialog";
import ProductStoreMatrixApi from "../api/ProductStoreMatrixApi";

const BuildProductMatrix = () => {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [mappings, setMappings] = useState({});
  const [isMatrixVisible, setMatrixVisible] = useState(false);
  const [missingItems, setMissingItems] = useState([]); // Track missing items
  const [inactiveItems, setInactiveItems] = useState([]); //Track Inactive items
  const [missingDialogOpen, setMissingDialogOpen] = useState(false); // Control dialog visibility

  const handleProductsFetched = async (
    fetchedProducts,
    missingItems,
    inactiveItems
  ) => {
    try {
      if (!Array.isArray(fetchedProducts)) {
        throw new Error("Fetched products are not valid");
      }

      // Deduplicate products by STYLE_COLOR
      const deduplicatedProducts = fetchedProducts.reduce((acc, product) => {
        const key = product.STYLE_COLOR;
        if (!acc[key]) {
          acc[key] = product;
        }
        return acc;
      }, {});

      const deduplicatedProductList = Object.values(deduplicatedProducts);
      setProducts(deduplicatedProductList);

      // Fetch mappings
      if (deduplicatedProductList.length > 0) {
        const styleColors = deduplicatedProductList
          .map((p) => p.STYLE_COLOR)
          .join(",");

        const response = await ProductStoreMatrixApi.fetchAll({
          query: styleColors,
        });

        if (response?.data?.mappings) {
          const formattedMappings = response.data.mappings.reduce(
            (acc, mapping) => {
              acc[`${mapping.style_color}-${mapping.store_id}`] =
                mapping.is_selected;
              return acc;
            },
            {}
          );
          setMappings(formattedMappings);
        } else {
          setMappings({});
        }
      }

      // Filter missing and inactive items
      const uniqueMissingItems = [...new Set(missingItems)];
      const uniqueInactiveItems = [...new Set(inactiveItems)];

      setMissingItems(uniqueMissingItems);
      setInactiveItems(uniqueInactiveItems);

      if (uniqueMissingItems.length > 0 || uniqueInactiveItems.length > 0) {
        setMissingDialogOpen(true);
      }
    } catch (error) {
      console.error("Error in handleProductsFetched:", error);
    }
  };

  const handleStoresFetched = (fetchedStores) => {
    setStores(fetchedStores);
    setMatrixVisible(true); // Ensure matrix visibility is set only after stores are loaded
  };

  return (
    <Box sx={{ padding: 1 }}>
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        Build Store Matrix
      </Typography>
      <Divider sx={{ marginY: 2 }} />
      {!isMatrixVisible ? (
        <ProductSearch
          onProductsFetched={handleProductsFetched}
          onStoresFetched={handleStoresFetched}
        />
      ) : (
        <>
          {/* Missing Items Dialog */}
          <CustomDialog
            open={missingDialogOpen}
            onClose={() => setMissingDialogOpen(false)}
            title="Missing and Inactive Items"
            message={
              <>
                {missingItems.length > 0 && (
                  <div>
                    <strong>Missing Items:</strong>
                    <ul>
                      {missingItems.map((item, index) => (
                        <li key={`missing-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {inactiveItems.length > 0 && (
                  <div>
                    <strong>Inactive Items:</strong>
                    <ul>
                      {inactiveItems.map((item, index) => (
                        <li key={`inactive-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            }
            type="warning"
            primaryButtonText="OK"
          />

          <ProductStoreMatrix
            products={products}
            stores={stores}
            mappings={mappings}
          />
        </>
      )}
    </Box>
  );
};

export default BuildProductMatrix;
